import { useCallback, useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';

import {
  Button,
  Icon,
  Restricted,
  Select,
  Table,
  TableColumn,
} from '@components';
import { User } from 'interfaces/User';
import AccessGroupAPICaller from 'services/api/accessGroups';
import UsersAPICaller from 'services/api/user';

type Props = {
  groupName: string;
  groupId: string;
  onUpdate: () => void;
};

export default function AccessUsersTable(props: Props) {
  const [usersFromGroup, setUsersFromGroup] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const { watch, register, setValue, handleSubmit } = useForm();
  const listUsersByQuery = watch('userName');

  const fetchUsersFromGroup = useCallback(async () => {
    const group = await AccessGroupAPICaller.fetchGroup(props.groupId);
    setUsersFromGroup(group.users);
  }, [props.groupId]);

  const fetchUsers = useCallback(async () => {
    const response = await UsersAPICaller.fetchUsers({
      q: listUsersByQuery,
      accessGroupId: `not:${props.groupId}`,
      size: 100,
    });

    setUsers(response.result);
  }, [listUsersByQuery, props.groupId]);

  useEffect(() => {
    fetchUsersFromGroup();
  }, [fetchUsersFromGroup]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const onSubmit = (async (data: { userId: string }) => {
    if (!data.userId) return;
    setIsAddingUser(true);
    await UsersAPICaller.setUserGroup(data.userId, props.groupId).finally(() =>
      setIsAddingUser(false)
    );
    await fetchUsersFromGroup();
    await fetchUsers();
    setValue('userId', '');
    setValue('userName', '');
    props.onUpdate();
  }) as SubmitHandler<FieldValues>;

  return (
    <div className="p-s-300">
      <h4 className="mb-s-200 color-primary">
        Usuários de <span>{props.groupName}</span>
      </h4>
      <Restricted context="users" resource="groups">
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="row justify-between pb-s-200">
            <Select
              options={users.map((user) => ({
                label: user.name,
                value: user.id,
              }))}
              onSelect={(option) => {
                if (!option) return;
                setValue('userName', option.label);
                setValue('userId', option.value);
              }}
              placeholder="Pesquisar usuário"
              form={register('userName')}
              value={watch('userName')}
              className="col-sm-4 mr-s-200"
            />
            <Button
              isLoading={isAddingUser}
              type="submit"
              weight="bold"
              design="outlined"
              prefixes={<Icon>group_add</Icon>}
              className="col-sm-2"
            >
              Adicionar
            </Button>
          </div>
        </form>
      </Restricted>
      <Table data={usersFromGroup} showColumnSelector={false}>
        <TableColumn
          key={`${props.groupId}-name`}
          header="Nome"
          fromKey="name"
          container
          ellipsis
          cellAlign="start"
        />
        <TableColumn
          key={`${props.groupId}-email`}
          header="Email"
          fromKey="email"
          container
          ellipsis
          cellAlign="start"
        />
        <TableColumn
          key={`${props.groupId}-actions`}
          header="Ações"
          disableSorting
          width="6%"
          container
          cellAlign="end"
          fromKey="id"
          render={(userId) => (
            <div className="d-flex">
              <Restricted context="users" resource="groups">
                <Button
                  weight="bold"
                  design="transparent"
                  prefixes={<Icon>delete</Icon>}
                  onClick={async () => {
                    await UsersAPICaller.removeUserGroup(userId);
                    props.onUpdate();
                    await fetchUsersFromGroup();
                    await fetchUsers();
                  }}
                />
              </Restricted>
            </div>
          )}
        />
      </Table>
    </div>
  );
}
